import app from 'flarum/common/app';
import { extend } from 'flarum/common/extend';
import taskListPlugin from './markdown-it/taskListPlugin';
export default function configureRichText() {
  flarum.reg.onLoad('fof-rich-text', 'common/tiptap/tiptap', _ref => {
    let {
      ListItem
    } = _ref;
    /**
     * Extends StarterKit's ListItem with a nullable `checked` attribute.
     * null  = plain list item (no checkbox rendered)
     * true/false = task item (checkbox rendered via NodeView)
     *
     * Using ListItem.extend() ensures our additions are properly merged into
     * the same node type — no duplicate name warnings, no schema conflicts.
     */
    const CheckableListItem = ListItem.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          checked: {
            default: null,
            parseHTML: el => {
              if (!el.hasAttribute('data-task')) return null;
              return el.getAttribute('data-checked') === '' ? true : false;
            },
            renderHTML: attrs => {
              if (attrs.checked === null) return {};
              return {
                'data-task': '',
                'data-checked': attrs.checked ? '' : null
              };
            }
          }
        };
      },
      addNodeView() {
        return _ref2 => {
          let {
            node,
            getPos,
            editor
          } = _ref2;
          const li = document.createElement('li');
          if (node.attrs.checked === null) {
            // Plain list item — contentDOM is the li itself
            return {
              dom: li,
              contentDOM: li
            };
          }

          // Task item — set attribute so CSS can target it
          li.setAttribute('data-task', '');
          const label = document.createElement('label');
          label.contentEditable = 'false';
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.checked = node.attrs.checked;
          input.addEventListener('mousedown', e => e.preventDefault());
          input.addEventListener('change', e => {
            const checked = e.target.checked;
            if (editor.isEditable && typeof getPos === 'function') {
              editor.chain().focus(undefined, {
                scrollIntoView: false
              }).command(_ref3 => {
                let {
                  tr
                } = _ref3;
                const pos = getPos();
                if (typeof pos !== 'number') return false;
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  checked
                });
                return true;
              }).run();
            }
          });
          const content = document.createElement('div');
          label.append(input);
          li.append(label, content);
          return {
            dom: li,
            contentDOM: content,
            update: updatedNode => {
              if (updatedNode.type !== node.type) return false;
              input.checked = updatedNode.attrs.checked ?? false;
              return true;
            }
          };
        };
      }
    });
    extend('ext:fof/rich-text/common/tiptap/TiptapEditorDriver', 'buildExtensions', function (items) {
      // Replace StarterKit's listItem with our extended version.
      // StarterKit is added as 'starterKit' — we configure it to disable its
      // built-in listItem and add ours separately so Tiptap sees one definition.
      const starterKit = items.get('starterKit');
      items.add('starterKit', starterKit.configure({
        ...starterKit.options,
        listItem: false
      }));
      items.add('listItem', CheckableListItem);
    });
  });
  extend('ext:fof/rich-text/common/tiptap/markdown/MarkdownParserBuilder', 'buildTokenizer', function (tokenizer) {
    tokenizer.use(taskListPlugin);
  });
  extend('ext:fof/rich-text/common/tiptap/markdown/MarkdownParserBuilder', 'buildTokens', function (tokens) {
    Object.assign(tokens, {
      task_item: {
        block: 'listItem',
        getAttrs: tok => ({
          checked: tok.attrGet('checked') === 'true'
        })
      }
    });
  });
  extend('ext:fof/rich-text/common/tiptap/markdown/MarkdownSerializerBuilder', 'buildNodes', function (nodes) {
    const originalListItem = nodes.listItem;
    nodes.listItem = function (state, node) {
      if (node.attrs.checked !== null) {
        state.text(node.attrs.checked ? '[x] ' : '[ ] ', false);
      }
      originalListItem(state, node);
    };
  });
  extend('ext:fof/rich-text/common/components/TiptapMenu', 'items', function (items) {
    const CommandButtonClass = flarum.reg.get('fof-rich-text', 'common/components/CommandButton');
    items.add('check_list', m(CommandButtonClass, {
      type: "check_list",
      icon: "fas fa-check-square",
      tooltip: app.translator.trans('fof-checklist.lib.composer.checklist_tooltip'),
      editor: this.attrs.editor,
      command: editor => toggleChecklist(editor)
    }), 10);
  });
}
function toggleChecklist(editor) {
  const {
    state,
    view
  } = editor;
  const {
    $from
  } = state.selection;
  const listItemType = state.schema.nodes.listItem;
  if (!listItemType) return;

  // Find nearest listItem ancestor
  let nodeDepth = -1;
  for (let d = $from.depth; d >= 0; d--) {
    if ($from.node(d).type === listItemType) {
      nodeDepth = d;
      break;
    }
  }
  if (nodeDepth < 0) {
    // Not in a list — create a bullet list then convert the new item
    editor.chain().focus().toggleBulletList().run();
    const state2 = editor.state;
    const {
      $from: $from2
    } = state2.selection;
    for (let d = $from2.depth; d >= 0; d--) {
      if ($from2.node(d).type === state2.schema.nodes.listItem) {
        const pos = $from2.before(d);
        editor.view.dispatch(state2.tr.setNodeMarkup(pos, undefined, {
          checked: false
        }));
        return;
      }
    }
    return;
  }
  const nodePos = $from.before(nodeDepth);
  const currentChecked = $from.node(nodeDepth).attrs.checked;
  if (currentChecked === null) {
    view.dispatch(state.tr.setNodeMarkup(nodePos, undefined, {
      checked: false
    }));
  } else {
    view.dispatch(state.tr.setNodeMarkup(nodePos, undefined, {
      checked: null
    }));
  }
}
flarum.reg.add('fof-checklist', 'common/configureRichText', configureRichText);